'use client';

import { ENUM_UTILS, MarketId } from '@velocity-exchange/common';
import { MarketType } from '@velocity-exchange/sdk';
import useAppParams from 'src/hooks/useAppParams';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import { useCurrentAccountPositions } from 'src/hooks/query/accountTradingData/usePositions';
import StatusBar from 'src/components/primitives/StatusBar/StatusBar';
import { useMemo } from 'react';

export const ArbitraryStatusBar = () => {
	const data = useAppParams();
	const currentUserAccount = useCurrentUserAccount();
	const openPerpPositions = useCurrentAccountPositions();

	const status = data?.statusBar?.status ?? 'warn';
	const message = data?.statusBar?.message;
	const showOnlyForInvolvedUsers = data?.statusBar?.showOnlyForInvolvedUsers;

	const isUserInvolvedInMarket = useMemo(() => {
		if (
			!data?.statusBar?.showOnlyForInvolvedUsers ||
			!currentUserAccount ||
			!data?.statusBar?.involvedMarketIndexes ||
			!data?.statusBar?.involvedMarketTypes
		)
			return false;

		const involvedMarketIndexes = data.statusBar.involvedMarketIndexes;
		const involvedMarketTypes = data.statusBar.involvedMarketTypes;

		if (involvedMarketIndexes.length !== involvedMarketTypes.length)
			return false;

		const involvedMarkets = involvedMarketIndexes.map(
			(index, i) =>
				new MarketId(
					index,
					ENUM_UTILS.toObj(involvedMarketTypes[i]) as MarketType
				)
		);

		const perpMarkets = involvedMarkets.filter((market) => market.isPerp);
		const spotMarkets = involvedMarkets.filter((market) => market.isSpot);

		const isUserInvolvedInPerpMarkets = perpMarkets.some((market) =>
			openPerpPositions.some(
				(position) => position.marketIndex === market.marketIndex
			)
		);
		const isUserInvolvedInSpotMarkets = spotMarkets.some((market) =>
			currentUserAccount.spotBalances.some(
				(balance) => balance.asset.marketIndex === market.marketIndex
			)
		);

		return isUserInvolvedInPerpMarkets || isUserInvolvedInSpotMarkets;
	}, [
		currentUserAccount,
		openPerpPositions,
		data?.statusBar?.showOnlyForInvolvedUsers,
		data?.statusBar?.involvedMarketIndexes,
		data?.statusBar?.involvedMarketTypes,
	]);

	if (!message || (showOnlyForInvolvedUsers && !isUserInvolvedInMarket)) {
		return null;
	}

	const variant = (() => {
		if (status === 'warn') {
			return 'warning';
		}

		if (status === 'error') {
			return 'error';
		}

		return 'info';
	})();

	return <StatusBar variant={variant}>{message}</StatusBar>;
};
